<template>
  <div>
    <div>
      <md-field :class="{ 'md-invalid': noHits }">
        <md-textarea
          ref="text"
          :value="text"
          @input="updateText"
          placeholder="商品コードを改行区切りで入力してください"
        ></md-textarea>
        <span class="md-error">商品が見つかりませんでした</span>
      </md-field>
      <div v-if="itemSearchCount > 0">
        {{ itemSearchCount }}件中 {{ itemFoundCount }}商品見つかりました
      </div>
    </div>
    <div class="items-container">
      <item-list
        :items="items"
        :selected.sync="selected"
        category-visible
        selectable
      />
      <div v-if="itemFountExceedsLimit" class="message-max-count-exceeds">
        検索結果上位{{ maxSearchCount }}件を表示しています
      </div>
      <div class="fetching" v-if="fetching">
        <spinner />
      </div>
    </div>
    <div class="button-group">
      <gl-button
        dense
        primary
        icon-pre="list-add"
        class="button-group__button"
        :disabled="items.length === 0"
        @click=";(selected = items), $emit('selected', selected)"
      >
        All
      </gl-button>
      <gl-button
        dense
        primary
        class="button-group__button"
        :disabled="selected.length === 0"
        @click="$emit('selected', selected)"
      >
        <gl-icon-list-add />
      </gl-button>
    </div>
  </div>
</template>

<script>
import { Subject, from, of } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  tap,
  map,
  switchMap,
} from 'rxjs/operators'
import ItemList from '@/components/ItemList.vue'
import Spinner from '@/components/Spinner.vue'

export default {
  components: { ItemList, Spinner },
  props: {
    /**
     * 企業
     */
    company: {
      type: String,
      required: true,
    },
    /**
     * 検索時、最大で表示する商品数
     *
     * 検索結果（Array）の先頭x件を表示します。並び順はリポジトリがどの
     * 順番で返すかに依存します。
     */
    maxSearchCount: {
      type: Number,
      default: 100,
    },
    /**
     * 使用可能カテゴリ
     */
    availableItemCategory: {
      type: Object,
    },
  },
  data() {
    return {
      /**
       * 検索テキスト
       */
      text: undefined,
      /**
       * 検索テキスト変更Subject
       */
      textSubject: new Subject(),
      /**
       * 検索中
       */
      fetching: false,
      /**
       * 検索テキストでのヒットなし
       */
      noHits: false,
      /**
       * 検索した商品数
       */
      itemFoundCount: 0,
      /**
       * 検索した商品数
       */
      itemSearchCount: 0,
      /**
       * 選択肢に表示する商品
       */
      items: [],
      /**
       * 選択されている商品
       */
      selected: [],
      /**
       * Snackbarプロパティ
       */
      snackbar: {
        show: false,
        duration: 1500,
        message: '',
      },
      // /**
      //  * 使用可能カテゴリ
      //  */
      // availableItemCategories: undefined,
    }
  },
  // watch: {
  //   company: {
  //     immediate: true,
  //     async handler(company) {
  //       if (company) {
  //         this.availableItemCategories = await this.$repositories
  //           .get('accountAvailableItemCategories')
  //           .getOne(this.$store.state.account.loginId, company)
  //       }
  //     },
  //   },
  // },
  computed: {
    /**
     * 検索した結果が表示最大件数をオーバーしているか
     */
    itemFountExceedsLimit() {
      return this.itemFoundCount > this.maxSearchCount
    },
  },
  methods: {
    /**
     * 入力テキスト変更
     */
    updateText(v) {
      this.text = v
      this.textSubject.next(v)
    },
    /**
     * Snackbarの表示
     */
    showSnackbar(options) {
      this.snackbar.duration = options.duration || 1500
      this.snackbar.message = options.message
      this.snackbar.show = true
    },
  },
  mounted() {
    // エントリー名絞込のトリガーです
    // 絞込テキストの入力があるとfilterTextSubjectに通知をするので、
    // 適度なタイミング（xxx ms入力が止まっている and 前回と値が違う）で
    // 実際の絞り込み処理を行います。
    const subscription = this.textSubject
      .asObservable()
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        map(text =>
          text
            .split('\n')
            .filter(v => v)
            .map(v => v.trim()),
        ),
        tap(codes => {
          this.fetching = true
          this.itemSearchCount = codes.length
        }),
        switchMap(codes => {
          if (codes.length === 0) {
            return of([])
          } else {
            return from(
              this.$repositories.get('item').getByCode(this.company, codes, {
                categoryLevel: this.availableItemCategory.categoryLevel,
                categories: this.availableItemCategory.categories,
              }),
            )
          }
        }),
      )
      .subscribe(items => {
        this.noHits = items.length === 0
        this.itemFoundCount = items.length
        this.items = items.slice(0, this.maxSearchCount)
        this.selected = []
        this.fetching = false
      })

    this.$on('hook:beforeDestroy', () => subscription.unsubscribe())

    // 入力欄にフォーカスを自動で当てたいが、ダイアログ内にこのコンポーネントを置いた場合、
    // 少し待たないとfocusが効かなかった（$elではちゃんとinput要素が取れていたが）。
    setTimeout(() => {
      this.$refs.text.$el.focus()
    }, 500)
  },
}
</script>

<style lang="scss" scoped>
.items-container {
  position: relative;
  height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.message-max-count-exceeds {
  color: #ff7043; // TODO 色ちゃんとする
  width: 100%;
  text-align: center;
}

.fetching {
  background: rgba($color: #000000, $alpha: 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10;
}
</style>
